import GalileoAegis from '@tencent/aegis-web-sdk-v2';
import { getRuntimeContext } from '@/runtime/context';
import { DeployEnv } from '@/runtime';
import { getRequestBaseURL } from '@/request/standalone-base-url';
import {
  buildTraceApiConfig,
  filterGalileoLogs,
  handleApiResponse,
  MONITORING_ENDPOINT_PATTERNS,
} from './aegis-policy';
import { createMonitoringReporter, warnMonitoringFailure } from './aegis-reporter';
import { installDiagnosticSender } from './monitoring/diagnostics';

function createGalileoClient(): GalileoAegis | null {
  try {
    const runtime = getRuntimeContext();
    const releaseVersion = Array.from(document.getElementsByTagName('meta'))
      .find(element => element.name === 'x-release-id')
      ?.getAttribute('content') || undefined;

    // 监控需早于登录态加载，用户身份在登录完成后通过 setMonitoringUser 补充。
    const traceConfig = buildTraceApiConfig(
      runtime.observability.galileoTraceEnabled,
      getRequestBaseURL(runtime),
    );

    // 本地开发不创建 SDK，避免安装采集插件或将调试数据混入部署环境。
    const aegisEnv = runtime.observability.isDevelopmentBuild ? null : runtime.observability.galileoEnvironment;
    return aegisEnv === null ? null : new GalileoAegis({
      id: 'SDK-540800bc78eba6997056',
      hostUrl: { url: 'https://tgalileo.com/collect' },
      env: aegisEnv,
      version: releaseVersion,
      extField: {
        bizPlatform: runtime.backend.platform,
        backendTargetId: runtime.backend.targetId,
      },
      repeat: 100,
      plugin: {
        pv: true,
        aid: true,
        error: true,
        device: true,
        close: true,
        pagePerformance: true,
        webVitals: true,
        fId: true,
        spa: true,
        api: {
          apiDetail: true,
          retCodeHandler: handleApiResponse,
          ignoreUrls: [...MONITORING_ENDPOINT_PATTERNS],
          ...traceConfig,
        },
        apiSpeed: true,
        assetSpeed: true,
        session: true,
        blankScreen: runtime.deployment.stage === DeployEnv.TEST || runtime.deployment.stage === DeployEnv.PRE,
      },
      onBeforeSend: filterGalileoLogs,
    });
  } catch (error) {
    warnMonitoringFailure('initialization', error);
    return null;
  }
}

const galileoAegis = createGalileoClient();

export const aegis = createMonitoringReporter(galileoAegis);

installDiagnosticSender((diagnostic) => {
  if (diagnostic.kind === 'exception') {
    aegis.error(diagnostic.error);
  } else {
    aegis.reportEvent({
      name: 'frontend_diagnostic',
      ext1: diagnostic.code,
      ext2: diagnostic.source,
      ...(diagnostic.detail ? { ext3: diagnostic.detail } : {}),
    });
  }
});

export function setMonitoringUser(userName: string): void {
  aegis.setUser(userName);
}
